import { allOrdersPath, menuPath, userFundsPath, userOrdersMealsPath, userOrdersPathDate, userOrdersSnacksPath } from "../../../functions/src/admin/paths";
import { ORDER_CHECKOUT_STATUS_PARTIAL } from "../../../functions/src/api/constants";
import { draftOrderWithoutSetMenuMealLines, type OrderDayDeadlineContext } from "../../../functions/src/common/orderDeadlines";
import type { MealLine, Order, OrderCheckoutSession } from "../../../functions/src/types";
import { clearAbandonedOrderCheckout } from "../../api/funds";
import { auth, currentMenu, mainDatabase, schoolDatabase } from "../../api/firebase";
import { captureException } from "../../api/sentry";
import { normalizeOrderContent, orderHasContentLines, type BasketEntryKind } from "./useBasket";

export function hasCommittedCheckoutDays(session: OrderCheckoutSession): boolean {
    return Object.values(session.days ?? {}).some(d => d?.committed);
}

/** True when some basket days were already registered — fulfillment must resume, not re-quote. */
export function shouldAutoRecoverCheckoutSession(session: OrderCheckoutSession): boolean {
    return hasCommittedCheckoutDays(session);
}

/**
 * Stale partial-Viva session (quoted when wallet was lower) but wallet now covers the full total —
 * re-quote via initiateOrderCheckout instead of resuming fulfillment on the old split.
 */
export function shouldRequoteStaleCheckoutSession(session: OrderCheckoutSession, walletBalance: number): boolean {
    const netDue = session.netDue ?? 0;
    const hasCommitted = Object.values(session.days ?? {}).some(d => d?.committed);
    return !hasCommitted
        && (session.payAmount ?? 0) > 0
        && walletBalance + 0.005 >= netDue;
}

/** Pending quote still matches this basket — skip re-initiate so the same Viva order can be reused. */
export function checkoutSessionMatchesBasket(session: OrderCheckoutSession, entryDates: string[], payAmount: number): boolean {
    if (session.status === ORDER_CHECKOUT_STATUS_PARTIAL || hasCommittedCheckoutDays(session)) {
        return false;
    }
    const sessionDates = (session.days ?? []).map(d => d.date).sort().join(',');
    const basketDates = [...entryDates].sort().join(',');
    if (sessionDates !== basketDates) {
        return false;
    }
    return Math.abs((session.payAmount ?? 0) - payAmount) <= 0.005;
}

/**
 * Drop a failed credits-only checkout with nothing registered yet — unblocks a fresh quote/checkout.
 * Keeps sessions where any day was already committed.
 */
export async function clearAbandonedOrderCheckoutSession(session: OrderCheckoutSession | null | undefined): Promise<boolean> {
    if (!auth.currentUser?.uid || !session) return false;
    if (hasCommittedCheckoutDays(session)) return false;
    const { cleared } = await clearAbandonedOrderCheckout();
    return cleared;
}

async function getMenu1Key(date: string): Promise<string | undefined> {
    return (await mainDatabase.ref(`${menuPath(currentMenu)}/${date}/menu1/key`).once('value')).val();
}

/** New-day draft clear — mirrors Order.removeOrderFromUser (keeps prepaid package skeleton). */
async function clearNewDayDraft(uid: string, date: string): Promise<void> {
    const orderRef = schoolDatabase.ref(userOrdersPathDate(uid, date));
    const order: Order | null = (await orderRef.once('value')).val();
    if (!order) return;

    const meals = order.meals ?? {};
    const hasPackage = Object.values(meals).some(line => line?.isPackage);
    const menu1Key = hasPackage ? await getMenu1Key(date) : undefined;

    if (hasPackage && menu1Key) {
        await orderRef.child('snacks').remove();
        for (const [mealId, meal] of Object.entries(meals)) {
            if (meal?.isPackage) {
                await orderRef.child(`meals/${mealId}`).update({
                    packagePrice: 0,
                    key: menu1Key,
                    orderItemRemark: null,
                });
            } else {
                await orderRef.child(`meals/${mealId}`).remove();
            }
        }
        await orderRef.update({ price: null, timestamp: null, uid: null, orderRemarks: null });
    } else {
        await orderRef.remove();
    }
}

/** Change-day draft revert — mirrors Order.cancelChangeOrder. */
async function revertChangeDayDraft(uid: string, date: string): Promise<void> {
    const placed: Order | null = (await schoolDatabase.ref(allOrdersPath(uid, date)).once('value')).val();
    const orderRef = schoolDatabase.ref(userOrdersPathDate(uid, date));
    await orderRef.child('isChangingOrder').remove();
    if (placed) {
        await orderRef.set(placed);
    } else {
        await orderRef.remove();
    }
}

/** Drop one day from the basket (revert change or clear new draft). */
export async function removeBasketDay(date: string, kind: BasketEntryKind): Promise<void> {
    const uid = auth.currentUser?.uid;
    if (!uid) return;
    try {
        if (kind === 'change') {
            await revertChangeDayDraft(uid, date);
        } else {
            await clearNewDayDraft(uid, date);
        }
    } catch (error) {
        console.error('removeBasketDay failed:', error);
        captureException(error instanceof Error ? error : new Error(String(error)), { context: 'removeBasketDay', date, kind });
        throw error;
    }
}

type RemoveBasketLineArgs = {
    date: string;
    kind: BasketEntryKind;
    lineType: 'meal' | 'snack';
    lineId: string;
    placed: Order | null;
    /** Required when removing/updating a package meal line. */
    mealLine?: MealLine;
};

type UpdateBasketLineQuantityArgs = RemoveBasketLineArgs & {
    quantity: number;
};

/** Set meal/snack quantity in the draft; quantity 0 removes the line (same as removeBasketLine). */
export async function updateBasketLineQuantity({ date, kind, lineType, lineId, quantity, placed, mealLine }: UpdateBasketLineQuantityArgs): Promise<void> {
    if (quantity <= 0) {
        return removeBasketLine({ date, kind, lineType, lineId, placed, mealLine });
    }

    const uid = auth.currentUser?.uid;
    if (!uid) return;

    try {
        const lineRef = schoolDatabase.ref(
            lineType === 'meal' ? userOrdersMealsPath(uid, date) : userOrdersSnacksPath(uid, date)
        ).child(lineId);

        if (lineType === 'meal') {
            const updates: Partial<MealLine> = { quantity };
            if (mealLine?.quantitySide && mealLine.quantitySide > quantity) {
                updates.quantitySide = quantity;
            }
            if (mealLine?.quantityDessert && mealLine.quantityDessert > quantity) {
                updates.quantityDessert = quantity;
            }
            await lineRef.update(updates);
        } else {
            await lineRef.update({ quantity });
        }
    } catch (error) {
        console.error('updateBasketLineQuantity failed:', error);
        captureException(error instanceof Error ? error : new Error(String(error)), { context: 'updateBasketLineQuantity', date, lineType, lineId, quantity });
        throw error;
    }
}

/** Remove one meal/snack line from the draft; reverts or clears the day when nothing basket-worthy remains. */
export async function removeBasketLine({ date, kind, lineType, lineId, placed, mealLine }: RemoveBasketLineArgs): Promise<void> {
    const uid = auth.currentUser?.uid;
    if (!uid) return;

    try {
        const lineRef = schoolDatabase.ref(
            lineType === 'meal' ? userOrdersMealsPath(uid, date) : userOrdersSnacksPath(uid, date)
        ).child(lineId);

        // Prepaid package meals stay on the order — reset exchange instead of deleting the line
        if (lineType === 'meal' && mealLine?.isPackage) {
            const menu1Key = await getMenu1Key(date);
            if (menu1Key) {
                await lineRef.update({ key: menu1Key, orderItemRemark: null, packagePrice: 0 });
            } else {
                await lineRef.remove();
            }
        } else {
            await lineRef.remove();
        }

        const draft: Order | null = (await schoolDatabase.ref(userOrdersPathDate(uid, date)).once('value')).val();
        const stillHasLines = orderHasContentLines(draft);
        const syncedWithPlaced = kind === 'change' && placed && draft && normalizeOrderContent(draft) === normalizeOrderContent(placed);

        if (!stillHasLines || syncedWithPlaced) {
            if (kind === 'change') {
                await revertChangeDayDraft(uid, date);
            } else {
                await clearNewDayDraft(uid, date);
            }
        }
    } catch (error) {
        console.error('removeBasketLine failed:', error);
        captureException(error instanceof Error ? error : new Error(String(error)), { context: 'removeBasketLine', date, lineType, lineId });
        throw error;
    }
}

/** Drop set-menu meal lines from a basket day draft when daily meals / snacks can still checkout. */
export async function removeSetMenuMealsFromBasketDay(
    date: string,
    draft: Order,
    deadlineCtx: OrderDayDeadlineContext,
): Promise<void> {
    const uid = auth.currentUser?.uid;
    if (!uid) return;

    try {
        const trimmed = draftOrderWithoutSetMenuMealLines(draft, deadlineCtx);
        const meals = trimmed.meals ?? null;
        await schoolDatabase.ref(userOrdersPathDate(uid, date)).update({ meals });
    } catch (error) {
        console.error('removeSetMenuMealsFromBasketDay failed:', error);
        captureException(error instanceof Error ? error : new Error(String(error)), { context: 'removeSetMenuMealsFromBasketDay', date });
        throw error;
    }
}

/** Remove basket days whose order deadline has passed (server `expiredDates` list). */
export async function removeExpiredDaysFromBasket(
    expiredDates: string[],
    entries: { date: string; kind: BasketEntryKind }[],
): Promise<void> {
    const expiredSet = new Set(expiredDates);
    const toRemove = entries.filter(e => expiredSet.has(e.date));
    await Promise.all(toRemove.map(({ date, kind }) => removeBasketDay(date, kind)));
}

/** Remove every day currently in the basket. */
export async function emptyBasket(entries: { date: string; kind: BasketEntryKind }[]): Promise<void> {
    await Promise.all(entries.map(({ date, kind }) => removeBasketDay(date, kind)));
    if (!auth.currentUser?.uid) return;
    await clearAbandonedOrderCheckout();
}
